/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-06-29 15:55:10
 **/
/* eslint-disable @typescript-eslint/no-explicit-any */

import { DependencyList, useEffect, useRef } from "react";
import { IMessage, IFrame } from "@stomp/stompjs";
import { useEventHandlers, useEventMessage } from "@rfice/eventhandler";

import { sleep } from "../utilities";
import Network from "./network";
import useStompManager from "../network/useStompManager";

const Prefix = "STOMP_";
export const NetworkEvents = {
	onPrepare: Prefix + "onPrepare", // DO NOT USE
	onConnected: Prefix + "onConnected",
	onConnectFailure: Prefix + "onConnectFailure",
	onStompError: Prefix + "onStompError",
	onDisconnect: Prefix + "onDisconnect",
	onMessageReceived: Prefix + "onMessageReceived",
	onSubscribe: Prefix + "onSubscribe",
	onUnsubscribe: Prefix + "onUnsubscribe",
	onWebSocketClosed: Prefix + "onWebSocketClosed"
};
export type NetworkEvents = (typeof NetworkEvents)[keyof typeof NetworkEvents];

export type StompNetworkHookEvents = {
	onConnected?: (arg: any) => void;
	onConnectFailure?: (arg: any) => void;
	onDisconnect?: (arg: any) => void;
	onStompError?: (arg: IFrame) => void;
	onMessageReceived?: (message: Message.Frame<Message.StompMessageTypes>, topic: string, packet: IMessage) => void;
	onPacketReceived?: (packet: IMessage) => void;
	onSubscribe?: (topic: string) => void;
	onUnsubscribe?: (topic: string, error: any) => void;
	onWebSocketClosed?: (arg: any) => void;
};
class Queue<T = any> {
	private _queue: T[];

	constructor() {
		this._queue = [];
	}

	/**
	 * Adds an item to the queue.
	 *
	 * @param {T} item - The item to be added to the queue.
	 * @return {void} This method does not return a value.
	 */
	public push(item: T): void {
		this._queue.push(item);
	}

	/**
	 * Removes and returns the first element from the queue.
	 *
	 * @return {T | undefined} The removed element from the queue if present, otherwise undefined.
	 */
	public pop(): T | undefined {
		return this._queue.shift();
	}

	/**
	 * Retrieves the current size of the queue.
	 *
	 * @return The number of elements in the queue.
	 */
	public get size(): number {
		return this._queue!.length;
	}

	/**
	 * Checks if the collection is empty.
	 *
	 * @return {boolean} Returns true if the collection is empty, otherwise false.
	 */
	public get empty(): boolean {
		return this.size <= 0;
	}

	/**
	 * Retrieves the current state of the queue.
	 *
	 * @return {T[]} The array representing the current elements in the queue.
	 */
	public get queue(): T[] {
		return this._queue;
	}

	/**
	 * Clears all items from the internal queue, leaving it empty.
	 *
	 * @return {void}
	 */
	public clear(): void {
		this._queue = [];
	}
}

class EventQueue extends Queue<Callback> {
	constructor() {
		super();
	}

	public addToQueue(event: Callback) {
		this.push(event);
	}

	public processNextEvent() {
		if (!this.empty) {
			const event = this.pop();
			event?.();
		} else {
			console.debug("#EventQueue# Event queue is empty");
		}
	}

	public processAllEvents() {
		while (!this.empty) {
			this.processNextEvent();
		}
	}
}

export default function useStompClient(
	events?: StompNetworkHookEvents,
	deps: DependencyList = [],
	isPersistentMode = false
) {
	const stompNetwork = useStompManager();
	const eventQ = useRef(new EventQueue());

	useEventMessage(
		"stomp_message_received",
		(topic: string, packet: IMessage, message: Message.Frame<Message.StompMessageTypes>) => {
			// console.info("#useStompClient# onMessageReceived", topic, packet, message);
			packet && events?.onPacketReceived?.(packet);
			events?.onMessageReceived?.(message, topic, packet);
		}
	);

	useEventHandlers(
		{
			[NetworkEvents.onConnected](message) {
				events?.onConnected?.(message);
			},
			[NetworkEvents.onConnectFailure](message) {
				events?.onConnectFailure?.(message);
			},
			[NetworkEvents.onDisconnect](message) {
				events?.onDisconnect?.(message);
			},
			[NetworkEvents.onStompError](message: IFrame) {
				events?.onStompError?.(message);
			},
			[NetworkEvents.onMessageReceived](
				topic: string,
				packet: IMessage,
				message: Message.Frame<Message.StompMessageTypes>
			) {
				eventQ.current.addToQueue(async () => {
					packet && events?.onPacketReceived?.(packet);
					events?.onMessageReceived?.(message, topic, packet);
				});
			},
			[NetworkEvents.onSubscribe](topic) {
				// console.info("#useStompClient# onSubscribe", topic);
				events?.onSubscribe?.(topic);
			},
			[NetworkEvents.onUnsubscribe](topic, error) {
				// console.info("#useStompClient# onUnsubscribe", topic);
				events?.onUnsubscribe?.(topic, error);
			},
			[NetworkEvents.onWebSocketClosed](message: IFrame) {
				events?.onWebSocketClosed?.(message);
			}
		},
		[...deps]
	);

	const process_next_packet = async () => {
		eventQ.current.processAllEvents();

		await sleep(100);
		process_next_packet();
	};

	useEffect(() => {
		process_next_packet();
		return () => {
			isPersistentMode && Network.unsubscribe(Network.topics);
		};
	}, []);

	return stompNetwork;
}
